/**
 * Refresh Stage 3: Internal Link Candidates
 *
 * Displays internal link candidates selected by the LLM, enriched with SERP
 * context (content gaps, PAA questions). Shows which links are new vs. already
 * present in the existing article.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument, InternalLinkCandidate } from '../types';

function InputSummary() {
  const { document } = useArticleContext();
  const primary = document?.targeting?.keywords?.primary;
  const serp = document?.targeting?.serp as any;
  const decision = serp?.overview?.decision;

  return (
    <div className="text-xs text-gray-600 bg-gray-50 rounded border p-2 space-y-1">
      <div>
        <span className="text-gray-400">Primary keyword:</span>{' '}
        {(primary as any)?.keyword || primary || 'Not yet selected'}
      </div>
      {decision && (
        <div>
          <span className="text-gray-400">SERP decision:</span>{' '}
          <span className="font-medium">{decision}</span>
        </div>
      )}
    </div>
  );
}

function LinkCard({ link, index }: { link: InternalLinkCandidate; index: number }) {
  const isNew = link.is_new !== false;
  return (
    <div className={`text-xs border rounded px-3 py-2 ${isNew ? 'border-gray-200' : 'border-gray-100 bg-gray-50'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-800 truncate">{link.target_page}</span>
            {!isNew && (
              <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-500">already linked</span>
            )}
          </div>
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline truncate block"
          >
            {link.url}
          </a>
        </div>
        {link.relevance_score != null && (
          <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${
            link.relevance_score >= 9 ? 'bg-green-100 text-green-700' :
            link.relevance_score >= 7 ? 'bg-blue-100 text-blue-700' :
            'bg-gray-100 text-gray-600'
          }`}>
            {link.relevance_score}/10
          </span>
        )}
      </div>
      {link.suggested_anchor && (
        <div className="mt-1 text-gray-500">
          <span className="text-gray-400">Anchor:</span> &ldquo;{link.suggested_anchor}&rdquo;
        </div>
      )}
      {link.reasoning && (
        <div className="mt-0.5 text-gray-500 italic">{link.reasoning}</div>
      )}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const links = doc.content_research?.internal_link_candidates;
  if (!links || links.length === 0) {
    return <div className="text-sm text-gray-500">No internal link candidates yet.</div>;
  }

  const newLinks = links.filter(l => l.is_new !== false);
  const existingLinks = links.filter(l => l.is_new === false);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span>{links.length} candidates</span>
        {newLinks.length > 0 && <span className="text-green-600">{newLinks.length} new</span>}
        {existingLinks.length > 0 && <span>{existingLinks.length} already linked</span>}
      </div>

      {newLinks.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1.5">New link opportunities</div>
          <div className="space-y-1">
            {newLinks.map((link, i) => (
              <LinkCard key={link.url || i} link={link} index={i} />
            ))}
          </div>
        </div>
      )}

      {existingLinks.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-400 mb-1.5">Already linked</div>
          <div className="space-y-1">
            {existingLinks.map((link, i) => (
              <LinkCard key={link.url || i} link={link} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function RefreshLinksV2() {
  return (
    <StagePanel
      stageId="refresh_5_links"
      title="Refresh: Internal Links"
      description="Selects internal link candidates from the content inventory, informed by SERP competitive analysis."
      isLlmStage
      renderOutput={renderOutput}
    >
      <InputSummary />
    </StagePanel>
  );
}
